import { Stack, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { Dialog } from "components/Dialog";
import { FireDepartmentPicker } from "components/FireDepartmentPicker";
import { IssueListButton } from "components/Forms/IssueListButton";
import { SupplementalFireDepartmentSection } from "components/Forms/SupplementalFireDepartmentSection";
import { SupplementalLepcSection } from "components/Forms/SupplementalLepcSection";
import { LepcPicker } from "components/LepcPicker";
import { PrimaryAgencyPicker } from "components/LocalAgencies/PrimaryAgencyPicker";
import { FacilityInput, Issue } from "generated-graphql/graphql";
import { transform } from "hooks/transform/transformFacility";
import { useValidatingForm } from "hooks/useValidatingForm";
import { isEqual } from "lodash-es";
import { useCallback, useMemo, useState } from "react";
import { FormProvider } from "react-hook-form";
import { useFacilityInputValidation } from "routes/Customer/Chemicals/Report/validationHooks/useFacilityInputValidation";
import { hasCriticalIssues } from "util/forms";

import { FacilityDetailsInput } from "../FacilityProfile";
import { useFacility, useFacilityIssues, useUpdateFacilityProfile } from "../useFacility";
import { LOCAL_AGENCIES_SECTION_ICON } from "./LocalAgenciesSection";

export type AgencyType = "lepc" | "fireDepartment";

function useLocalAgencyIssues(agencyType: AgencyType) {
  const key = agencyType === "lepc" ? "lepcId" : "fireDepartmentId";
  return useFacilityIssues((issue) => issue.modelName === "Facility" && issue.key === key);
}

export function EditLocalAgenciesDialog({
  onClose,
  agencyType,
}: {
  onClose: () => void;
  agencyType: AgencyType;
}) {
  const alerts = useAlerts();
  const { data } = useFacility();
  const facility = data?.facility;
  const [updateFacility, { loading: saving }] = useUpdateFacilityProfile();
  const isCA = facility?.state === "CA";

  const persistedIssues = useLocalAgencyIssues(agencyType);

  const defaultValues = useMemo<FacilityDetailsInput>(
    () => ({
      name: facility?.name ?? "",
      state: facility?.state ?? null,
      county: facility?.county ?? null,
      streetAddress1: facility?.streetAddress1 ?? null,
      streetAddress2: facility?.streetAddress2 ?? null,
      city: facility?.city ?? null,
      zip: facility?.zip ?? null,
      country: facility?.country ?? null,
      latitude: facility?.latitude ?? null,
      longitude: facility?.longitude ?? null,
      phone: facility?.phone ?? null,
      phone24Hour: facility?.phone24Hour ?? null,
      lepcId: facility?.lepc?.id ?? null,
      fireDepartmentId: facility?.fireDepartment?.id ?? null,
      supplementalLepcIds: facility?.supplementalLepcs?.map((sl) => sl.lepc.id) ?? [],
      supplementalFireDepartmentIds:
        facility?.supplementalFireDepartments?.map((sfd) => sfd.fireDepartment.id) ?? [],
    }),
    [facility]
  );

  const pickField = agencyType === "lepc" ? "lepcId" : "fireDepartmentId";
  const validator = useFacilityInputValidation({
    facilityId: facility?.id ?? "",
    reportId: undefined,
    pick: [pickField],
  });

  const formValidator = useCallback(
    async (input: FacilityDetailsInput): Promise<Issue[]> => {
      const issues = await validator(input);
      // Filter out state field issues that leak through because the
      // compliance engine's pick filter doesn't gate state field validation.
      return issues.filter((issue) => issue.modelName !== "FacilityStateField");
    },
    [validator]
  );

  const form = useValidatingForm<FacilityDetailsInput>(
    defaultValues,
    persistedIssues,
    formValidator
  );
  const { handleSubmit, formState, issues, control, watch } = form;

  const [showConfirmClose, setShowConfirmClose] = useState(false);

  const currentValues = form.watch();
  const hasUnsavedChanges = useMemo(() => {
    const normalizeValue = (val: unknown): unknown => {
      if (val === "" || val === undefined) return null;
      if (Array.isArray(val)) return val.map(normalizeValue);
      if (val !== null && typeof val === "object") {
        return Object.fromEntries(Object.entries(val).map(([k, v]) => [k, normalizeValue(v)]));
      }
      return val;
    };
    return !isEqual(normalizeValue(currentValues), normalizeValue(defaultValues));
  }, [currentValues, defaultValues]);

  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  }, [hasUnsavedChanges, onClose]);

  const lepcId = watch("lepcId");
  const fireDepartmentId = watch("fireDepartmentId");
  const supplementalLepcIds = watch("supplementalLepcIds");
  const supplementalFireDepartmentIds = watch("supplementalFireDepartmentIds");

  const hasEmptySupplementalIds =
    (supplementalLepcIds ?? []).some((id) => !id) ||
    (supplementalFireDepartmentIds ?? []).some((id) => !id);

  const onSubmit = useCallback(
    async (formData: FacilityDetailsInput) => {
      if (!facility) return;

      const input = transform(formData);
      const facilityUpdate: Partial<FacilityInput> =
        agencyType === "lepc"
          ? {
              lepcId: input.lepcId,
              supplementalLepcIds: formData.supplementalLepcIds,
            }
          : {
              fireDepartmentId: input.fireDepartmentId,
              supplementalFireDepartmentIds: formData.supplementalFireDepartmentIds,
            };

      try {
        await updateFacility({
          variables: {
            id: facility.id,
            facility: facilityUpdate as FacilityInput,
          },
        });
        const label = agencyType === "lepc" ? (isCA ? "CUPA" : "LEPC") : "Fire Department";
        alerts.success(`${label} saved successfully.`);
        onClose();
      } catch (err) {
        alerts.error("Error saving agency information.");
        console.info("Error saving agency information.", err);
      }
    },
    [facility, updateFacility, alerts, onClose, agencyType, isCA]
  );

  const title = agencyType === "lepc" ? (isCA ? "CUPA" : "LEPC") : "Fire Department";

  return (
    <>
      <Dialog
        open
        title={
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="h6">Edit</Typography>
            <Typography variant="h6">{LOCAL_AGENCIES_SECTION_ICON}</Typography>
            <Typography variant="h6">{title}</Typography>
          </Stack>
        }
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        primaryActionButtonProps={{
          loading: saving,
          disabled: !hasUnsavedChanges || hasCriticalIssues(issues) || hasEmptySupplementalIds,
          onClick: () => handleSubmit(onSubmit)(),
        }}
        additionalPrimaryActionElements={[
          <IssueListButton issues={issues} key="issue-list-button" />,
        ]}
      >
        <FormProvider {...form}>
          <form>
            <Stack spacing={3}>
              {agencyType === "lepc" && (
                <>
                  <PrimaryAgencyPicker
                    name="lepcId"
                    control={control}
                    facilityState={facility?.state ?? undefined}
                    facilityCounty={facility?.county ?? undefined}
                    facilityLatitude={facility?.latitude}
                    facilityLongitude={facility?.longitude}
                    helperText="Primary: this agency will be included in your report in the portal."
                    PickerComponent={LepcPicker}
                    existingAgency={facility?.lepc}
                    label={isCA ? "CUPA" : "LEPC"}
                    pickerDescription={
                      isCA
                        ? "Choose from the list of CUPAs in your state. If a CUPA does not have a latitude/longitude on file or if it has a P.O. Box, it will appear at the top of this list without a distance. If you need to report to multiple CUPAs, you can add more below."
                        : "Choose from the list of LEPCs in your state. If an LEPC does not have a latitude/longitude on file or if it has a P.O. Box, it will appear at the top of this list without a distance. If you need to report to multiple LEPCs, you can add more below."
                    }
                  />
                  <SupplementalLepcSection
                    disabled={!lepcId}
                    facilityState={facility?.state}
                    existingAgencies={facility?.supplementalLepcs}
                    primaryLepcId={lepcId}
                    facilityLatitude={facility?.latitude}
                    facilityLongitude={facility?.longitude}
                    label={isCA ? "CUPA" : "LEPC"}
                  />
                </>
              )}

              {agencyType === "fireDepartment" && (
                <>
                  <PrimaryAgencyPicker
                    name="fireDepartmentId"
                    control={control}
                    facilityState={facility?.state ?? undefined}
                    facilityCounty={facility?.county ?? undefined}
                    facilityLatitude={facility?.latitude}
                    facilityLongitude={facility?.longitude}
                    helperText="Primary: this agency will be included in your report in the portal."
                    PickerComponent={FireDepartmentPicker}
                    existingAgency={facility?.fireDepartment}
                    pickerDescription="Choose from the list of Fire Districts. Be sure to select the correct FD for your facility, which may not be the closest agency. If an FD does not have a latitude/longitude on file or if it has a P.O. Box, it will appear at the top of this list without a distance. If you need to report to multiple FDs, you can add more below."
                  />
                  <SupplementalFireDepartmentSection
                    disabled={!fireDepartmentId}
                    facilityState={facility?.state}
                    existingAgencies={facility?.supplementalFireDepartments}
                    primaryFireDepartmentId={fireDepartmentId}
                    facilityLatitude={facility?.latitude}
                    facilityLongitude={facility?.longitude}
                  />
                </>
              )}
            </Stack>
          </form>
        </FormProvider>
      </Dialog>
      <ConfirmDialog
        open={showConfirmClose}
        onClose={() => setShowConfirmClose(false)}
        onConfirm={onClose}
        header="You have unsaved changes. Are you sure you want to close?"
        confirmText="Close without saving"
      />
    </>
  );
}
